import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ListFilters } from '../../../../interfaces/user.interface';
import { buildHeadCells } from '../../../../utils/methods';
import {
  GENERIC_LOOKUP_KEYS,
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
  STATUS,
} from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import UserService from '../../../../services/user.service';
import RoleService from '../../../../services/role.service';
import CommonService from '../../../../services/common.service';
import { SortOrder, Modules, AuthRoutes } from '../../../../enums';

const List: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>({
    searchText: '',
    role: '',
    status: STATUS.active,
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    role: '',
    status: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.first_name,
      VALIDATION_FIELD_MAPPING.role,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.updated_at,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'userList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.first_name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.users,
    uri: Modules.resources + AuthRoutes.users,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: users,
    loading,
  } = useFetch({
    service: UserService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.userList[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      role: filters.role,
      status:
        filters.status === STATUS.active ? 1 : filters.status === STATUS.inactive ? 0 : '',
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.role,
      filters.status,
    ],
  });

  const { data: kpis } = useFetch({
    service: UserService,
    func: 'kpis',
    payload: null,
    dependency: [],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const { data: allRolesLookup } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      searchText: '',
    },
    dependency: [],
  });

  const { data: roles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.role },
    dependency: [dropdownSearchData.role],
  });

  const handleFilterschange = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.first_name);
    setFilters(newFilters);
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const headerSection = <TableHeaderSection total={total} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: users || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('users.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    users,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  ]);

  return (
    <>
      <Header />

      <Stats data={kpis} />

      <Filters
        allRoles={allRolesLookup}
        status={status}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        initialFilters={filters}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        roles={roles}
        onFilterChange={handleFilterschange}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;
